import React, { useState, useEffect, useCallback } from 'react';
import { Upload, Button, Table, Tag, Typography, Space, message, Spin, Popconfirm } from 'antd';
import {
  UploadOutlined,
  FileOutlined,
  DownloadOutlined,
  DeleteOutlined,
  FilePdfOutlined,
  FileWordOutlined,
  FileExcelOutlined,
  FileImageOutlined,
} from '@ant-design/icons';
import type { UploadProps } from 'antd';
import dayjs from 'dayjs';
import { documentApi, type DocumentDto } from '@/api/documentApi';
import type { RfoPhase } from '@/types/workflow';

const { Text } = Typography;

interface GateDocumentsProps {
  opportunityId: string;
  phase: RfoPhase;
  onUploadComplete?: () => void;
}

const getFileIcon = (fileType?: string) => {
  if (!fileType) return <FileOutlined />;
  if (fileType.includes('pdf')) return <FilePdfOutlined style={{ color: 'var(--color-error)' }} />;
  if (fileType.includes('word') || fileType.includes('document'))
    return <FileWordOutlined style={{ color: 'var(--color-primary)' }} />;
  if (fileType.includes('excel') || fileType.includes('spreadsheet'))
    return <FileExcelOutlined style={{ color: 'var(--color-success)' }} />;
  if (fileType.includes('image'))
    return <FileImageOutlined style={{ color: 'var(--color-purple)' }} />;
  return <FileOutlined />;
};

const formatFileSize = (bytes?: number): string => {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const GateDocuments: React.FC<GateDocumentsProps> = ({
  opportunityId,
  phase,
  onUploadComplete,
}) => {
  const [documents, setDocuments] = useState<DocumentDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);

  // All authenticated users can delete documents
  const canDelete = (_doc: DocumentDto) => true;

  const fetchDocuments = useCallback(async () => {
    setLoading(true);
    try {
      const docs = await documentApi.getByPhase(opportunityId, phase);
      setDocuments(docs);
    } catch (err) {
      console.error('Failed to fetch phase documents:', err);
      setDocuments([]);
    } finally {
      setLoading(false);
    }
  }, [opportunityId, phase]);

  useEffect(() => {
    fetchDocuments();
  }, [fetchDocuments]);

  const handleUpload: UploadProps['customRequest'] = async (options) => {
    const { file, onSuccess, onError } = options;
    setUploading(true);
    try {
      await documentApi.uploadFile(
        file as File,
        opportunityId,
        'OTHER', // Default category for gate artifacts
        phase,
      );
      message.success(`Uploaded ${(file as File).name}`);
      onSuccess?.({});
      fetchDocuments();
      onUploadComplete?.();
    } catch (err: any) {
      console.error('Upload failed:', err);
      message.error('Failed to upload file');
      onError?.(err);
    } finally {
      setUploading(false);
    }
  };

  const handleDownload = async (doc: DocumentDto) => {
    try {
      await documentApi.downloadFile(doc.id, doc.fileName);
    } catch {
      message.error('Failed to download file');
    }
  };

  const handleDelete = async (docId: string) => {
    try {
      await documentApi.deleteDocument(docId);
      message.success('File deleted');
      fetchDocuments();
      onUploadComplete?.();
    } catch {
      message.error('Failed to delete file');
    }
  };

  return (
    <div>
      <Space style={{ marginBottom: 8 }}>
        <Text strong style={{ fontSize: 13 }}>Gate Artifacts</Text>
        {documents.length > 0 && (
          <Tag color="blue">{documents.length} file{documents.length !== 1 ? 's' : ''}</Tag>
        )}
      </Space>

      {loading ? (
        <Spin size="small" />
      ) : (
        <>
          {documents.length > 0 && (
            <Table<DocumentDto>
              size="small"
              dataSource={documents}
              rowKey="id"
              pagination={false}
              style={{ marginBottom: 8 }}
              scroll={{ x: 580 }}
              columns={[
                {
                  title: 'File Name',
                  dataIndex: 'fileName',
                  key: 'fileName',
                  width: 220,
                  sorter: (a, b) => (a.fileName || '').localeCompare(b.fileName || ''),
                  render: (name: string, doc) => (
                    <Space size={6}>
                      {getFileIcon(doc.fileType)}
                      <Text
                        style={{ fontSize: 12, maxWidth: 180 }}
                        ellipsis={{ tooltip: name }}
                      >
                        {name}
                      </Text>
                    </Space>
                  ),
                },
                {
                  title: 'Size',
                  dataIndex: 'fileSizeBytes',
                  key: 'size',
                  width: 70,
                  sorter: (a, b) => (a.fileSizeBytes ?? 0) - (b.fileSizeBytes ?? 0),
                  render: (b: number) => <Text style={{ fontSize: 11, whiteSpace: 'nowrap' }}>{formatFileSize(b)}</Text>,
                },
                {
                  title: 'Uploaded By',
                  dataIndex: 'uploadedBy',
                  key: 'uploadedBy',
                  width: 110,
                  sorter: (a, b) => (a.uploadedBy || '').localeCompare(b.uploadedBy || ''),
                  render: (v: string) => (
                    <Text style={{ fontSize: 11 }} ellipsis={{ tooltip: v }}>
                      {v || '—'}
                    </Text>
                  ),
                },
                {
                  title: 'Date Uploaded',
                  dataIndex: 'createdAt',
                  key: 'createdAt',
                  width: 130,
                  defaultSortOrder: 'descend' as const,
                  sorter: (a, b) => {
                    const aT = a.createdAt ? dayjs(a.createdAt).unix() : 0;
                    const bT = b.createdAt ? dayjs(b.createdAt).unix() : 0;
                    return aT - bT;
                  },
                  render: (v: string) => (
                    <Text style={{ fontSize: 11, whiteSpace: 'nowrap' }}>
                      {v ? dayjs(v).format('MM/DD/YYYY h:mm A') : '—'}
                    </Text>
                  ),
                },
                {
                  title: 'Version',
                  dataIndex: 'currentVersion',
                  key: 'version',
                  width: 60,
                  sorter: (a, b) => (a.currentVersion ?? 0) - (b.currentVersion ?? 0),
                  render: (v: number) => <Text style={{ fontSize: 11 }}>v{v}</Text>,
                },
                {
                  title: 'Review',
                  dataIndex: 'reviewStatus',
                  key: 'reviewStatus',
                  width: 90,
                  render: (status: DocumentDto['reviewStatus'], doc) => {
                    if (status === 'APPROVED') {
                      return (
                        <Tag color="success" style={{ fontSize: 10, margin: 0 }}>
                          Approved v{doc.approvedVersion ?? doc.currentVersion}
                        </Tag>
                      );
                    }
                    if (status === 'STALE') {
                      return (
                        <Tag color="error" style={{ fontSize: 10, margin: 0 }}>
                          Stale — re-review
                        </Tag>
                      );
                    }
                    return (
                      <Tag style={{ fontSize: 10, margin: 0 }}>Draft</Tag>
                    );
                  },
                },
                {
                  title: '',
                  key: 'actions',
                  render: (_v, doc) => (
                    <Space size={4}>
                      <Button
                        size="small"
                        type="link"
                        icon={<DownloadOutlined />}
                        onClick={() => handleDownload(doc)}
                      />
                      {canDelete(doc) && (
                        <Popconfirm
                          title="Delete this file?"
                          onConfirm={() => handleDelete(doc.id)}
                          okText="Delete"
                          okType="danger"
                        >
                          <Button size="small" danger icon={<DeleteOutlined />} />
                        </Popconfirm>
                      )}
                    </Space>
                  ),
                },
              ]}
            />
          )}

          <Upload
            customRequest={handleUpload}
            showUploadList={false}
            multiple={true}
            openFileDialogOnClick={true}
          >
            <Button
              icon={<UploadOutlined />}
              size="small"
              loading={uploading}
              style={{ pointerEvents: 'auto' }}
            >
              Upload Artifacts
            </Button>
          </Upload>
        </>
      )}
    </div>
  );
};

export default GateDocuments;
